import { useState } from 'react';
import type { ReactNode } from 'react';
import { Partnership } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';

export interface PartnershipFormValues {
  partnerName: string;
  legalName: string;
  type: string;
  country: string;
  emirate: string;
  industry: string;
  status: string;
  website: string;
  notes: string;
}

const TYPES = ['strategic', 'agency', 'creator_partner', 'referral', 'production', 'technology', 'media', 'supplier', 'revenue_share', 'other'];
const STATUSES = ['active', 'pending', 'expired', 'terminated'];

function toValues(p?: Partnership | null): PartnershipFormValues {
  return {
    partnerName: p?.partnerName ?? '',
    legalName: p?.legalName ?? '',
    type: p?.type ?? 'other',
    country: p?.country ?? '',
    emirate: p?.emirate ?? '',
    industry: p?.industry ?? '',
    status: p?.status ?? 'pending',
    website: p?.website ?? '',
    notes: p?.notes ?? '',
  };
}

export function PartnershipFormDrawer({ partnership, onClose, onSubmit }: { partnership?: Partnership | null; onClose: () => void; onSubmit: (v: PartnershipFormValues) => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState<PartnershipFormValues>(toValues(partnership));
  const [error, setError] = useState('');

  function set<K extends keyof PartnershipFormValues>(key: K, val: PartnershipFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  return (
    <EntityDrawer title={partnership ? t('partnershipForm.editTitle') : t('partnershipForm.createTitle')} onClose={onClose}>
      <Field label={t('partnershipForm.partnerName')} required>
        <input className="text-input" value={values.partnerName} onChange={(e) => set('partnerName', e.target.value)} />
      </Field>
      <Field label={t('partnershipForm.legalName')}><input className="text-input" value={values.legalName} onChange={(e) => set('legalName', e.target.value)} /></Field>
      <Field label={t('partnershipForm.type')}>
        <select className="text-input" value={values.type} onChange={(e) => set('type', e.target.value)}>
          {TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
        </select>
      </Field>
      <Field label="Country"><input className="text-input" value={values.country} onChange={(e) => set('country', e.target.value)} /></Field>
      <Field label="Emirate"><input className="text-input" value={values.emirate} onChange={(e) => set('emirate', e.target.value)} /></Field>
      <Field label={t('partnershipForm.industry')}><input className="text-input" value={values.industry} onChange={(e) => set('industry', e.target.value)} /></Field>
      <Field label="Status">
        <select className="text-input" value={values.status} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`status.partnership.${s}`)}</option>)}
        </select>
      </Field>
      <Field label={t('partnershipForm.website')}><input className="text-input" value={values.website} onChange={(e) => set('website', e.target.value)} /></Field>
      <Field label="Notes"><textarea className="text-input" rows={3} value={values.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
      {error && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={() => (values.partnerName.trim() ? onSubmit(values) : setError('Partner name is required'))}>{t('common.save')}</button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
    </div>
  );
}
